import {
    Alert,
    Avatar,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Grid,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MOOD_OPTIONS } from '../components/DailyMoodCheckInModal';
import ApiClient from '../config/ApiClient';
import { DailyMoodCheckIn } from '../object-actions/types/types';

const UserCheckInsScreen: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [checkIns, setCheckIns] = useState<DailyMoodCheckIn[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [user, setUser] = useState<{ real_name?: string; first_name?: string; last_name?: string; username?: string; avatar?: string } | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!id) return;

            setLoading(true);
            setError(null);

            try {
                // Fetch user info
                const userResponse = await ApiClient.get(`/api/users/${id}`);
                if (userResponse.success && userResponse.data) {
                    setUser(userResponse.data);
                }

                // Fetch check-ins
                const checkInsResponse = await ApiClient.get<{ results: DailyMoodCheckIn[]; count: number }>(
                    `/api/daily-mood-checkins/?user_id=${id}`
                );

                if (checkInsResponse.success && checkInsResponse.data) {
                    setCheckIns(checkInsResponse.data.results || []);
                } else {
                    setError(checkInsResponse.error || 'Failed to load check-ins');
                }
            } catch (err) {
                setError('An error occurred while loading check-ins');
                console.error('Error fetching check-ins:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    const userName = user
        ? user.real_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username
        : 'User';

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {user?.avatar && (
                        <Avatar
                            src={user.avatar}
                            alt={userName}
                            sx={{ width: 64, height: 64 }}
                        />
                    )}
                    <Box>
                        <Typography variant="h4" component="h1">
                            {userName}'s Mood Check-ins
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {checkIns.length} check-in{checkIns.length !== 1 ? 's' : ''} recorded
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {checkIns.length === 0 ? (
                <Card>
                    <CardContent>
                        <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                            No check-ins found for this user.
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={2}>
                    {checkIns.map((checkIn) => {
                        const moodOption = MOOD_OPTIONS[checkIn.mood_choice];
                        return (
                            <Grid item xs={12} sm={6} md={4} key={checkIn.id}>
                                <Card>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                            <Typography variant="h3" component="span">
                                                {moodOption.emoji}
                                            </Typography>
                                            <Box>
                                                <Typography variant="h6" component="div">
                                                    {moodOption.shortText}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatDate(checkIn.check_in_date)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">
                                            {moodOption.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        </Container>
    );
};

export default UserCheckInsScreen;

